import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import Link from '@mui/material/Link';
import { LexicalEditor } from 'lexical';

import {
  CasBaseDialog,
  CasButton,
  CasConfirmationDialog,
  CasLoaderWithBackdrop,
  CasRHFLexicalRichTextEditor,
  CasRHFTextField,
} from 'src/compass/shared/components';
import { CasSecondaryButton } from 'src/compass/shared/components/CasButton/CasSecondaryButton';
import CasItemNavigator from 'src/compass/shared/components/CasItemNavigator/CasItemNavigator';
import CasLink from 'src/compass/shared/components/CasLink/CasLink';
import CasReactHookForm, {
  FormMethodType,
} from 'src/compass/shared/components/CasForm/CasReactHookForm';
import {
  editorToEmailHtml,
  htmlToEditorState,
} from 'src/compass/shared/components/CasLexicalRichTextEditor/lexical-playground/src/CasLexicalRichTextEditor';
import { PENDING_EMAILS_QUERY_KEY } from 'src/compass/shared/components/CasPendingEmails/usePendingEmails';
import useAddMutation from 'src/compass/shared/hooks/reactQuery/useAddMutation';
import useRetrieve from 'src/compass/shared/hooks/reactQuery/useRetrieve';
import useCasBoolean from 'src/compass/shared/hooks/useCasBoolean';
import { onSuccessEnqueueSnackbar } from 'src/compass/shared/util/onSuccessEnqueueSnackbar';
import { Terms } from 'src/compass/shared/util/terms';
import { IGeneralObjectType } from 'src/compass/site/util';
import {
  BATCH_CHANNEL_OPTIONS,
  BatchOpenRowWatcher,
  BatchRowList,
  BatchRowPane,
  CustomerContactsButton,
  DEFAULT_BODY_LABEL,
  DEFAULT_KEY,
  IBatchRow,
  IBatchTerms,
  MAX_SMS_TEXT_LENGTH,
  MySignatureSettingsDialog,
  RHFMessageCategory,
  RecipientOptionType,
  RowChannelType,
  SendAsType,
  SendChannelType,
  SmsSnapshotType,
  SmsTextAdjustmentType,
  SmsTextField,
  addressesFor,
  batchComposeSchema,
  getRowChannel,
  isRecipientsEdited,
  makeBodiesSmsSpamSafe,
  makeSmsSpamSafe,
  replaceBodyWithSms,
  smsChangedSince,
  splitBodyForSms,
  supportsChannel,
  toQueryPairs,
  withoutKey,
  BatchSmsPictures,
  findOversizedMessages,
  toTransport,
  RingCentralConnectAlert,
  RingCentralFromNumber,
  useConnectRingCentral,
  useRingCentralConnection,
  useBatchListView,
} from 'src/compass/application';

/*
  Batch Send from the Customers pages: one authored message, one copy per customer in the listing.

  The counterpart of SCE Reports' Batch Send, and built on the same shared parts - the row list,
  the row pane, the channel rules, the spam-safe confirmation. What it does NOT have is everything
  SCE Reports' batch carries because it is mailing a REPORT: no templates, no data objects, and no
  per-insured stamping of the body. A customer message is the sender's own words, sent to more
  than one customer at a time.
*/

// A pinned Send As, as the Defaults endpoint names the channel. Auto asks for no channel at all.
const CHANNEL_PARAM: Record<string, string | undefined> = {
  auto: undefined,
  email: 'Email',
  sms: 'Text',
};

// What a row stands for here, in the words this page uses - see IBatchTerms.
const CUSTOMER_TERMS: IBatchTerms = {
  noun: Terms.Customer.label.toLowerCase(),
  // Every listed row is a customer, so this cannot happen - stated for completeness rather than
  // because it is reachable.
  missingId: `No ${Terms.Customer.label.toLowerCase()} record for this row`,
  noContacts: 'No contact is included by default for this customer',
  contactsLabel: 'Contacts',
  emptyList: 'No customers match the current filters.',
};

interface Props {
  onClose: VoidFunction;
  // The listing's own url + filters, so the batch mails exactly the rows on screen.
  requestMetadata: IGeneralObjectType;
}

export default function CustomerBatchSendDialog({ onClose, requestMetadata }: Props) {
  const queryClient = useQueryClient();
  const signatureDialog = useCasBoolean();
  const confirmDialog = useCasBoolean();

  // Texts go out from the sender's own RingCentral line; while it is not connected, a batch with
  // any text in it cannot be sent. The rows keep their SMS badges, so the sender can see which.
  const { connected: ringCentralConnected } = useRingCentralConnection();

  const [formMethods, setFormMethods] = useState<FormMethodType>(null);
  const [masterEditor, setMasterEditor] = useState<LexicalEditor | null>(null);
  const [overrideEditor, setOverrideEditor] = useState<LexicalEditor | null>(null);

  /*
    Which view of the batch's message is on screen: its email Body, or its text. Purely an authoring
    view - it decides what the sender is editing, not who gets what; a message's channel comes from
    its own row. Either way it is ONE message, as a single send is: the view being edited is the
    message, and the other is translated from it (syncSmsFromBody / syncBodyFromSms), so the email
    and the text can never say different things.
  */
  const [batchChannel, setBatchChannel] = useState<SendChannelType>('email');

  /*
    A row's own Send As, when the sender has pinned one. Keyed by row like the other row maps, and
    absent means Auto - resolve from that row's recipients. Pinning is how an unresolved row gets
    settled without editing anybody's contacts.
  */
  const [rowChannels, setRowChannels] = useState<Record<string, SendAsType>>({});

  /*
    The open row's recipients as they stand in the form, reported by the watcher inside it. Held
    only so the row's badge can follow the sender's edits: everything else - what is sent, what
    blocks the send - still reads the committed recipients, which is what a row is left holding.
  */
  const [openRow, setOpenRow] = useState<{
    rowKey: string;
    to: RecipientOptionType[];
    cc: RecipientOptionType[];
  } | null>(null);

  /*
    What Send is about to do - computed on Send, applied on confirm: the spam-safe rewrites and the
    text bodies over the carrier's length cap. A single send rewrites and stops so the sender can
    look before pressing Send again; a batch cannot, because all but one of its text bodies are off
    screen, so it shows them all in the confirmation instead.
  */
  const [pendingSend, setPendingSend] = useState<{
    batchText: string;
    usesBatchText: boolean;
    rowTexts: Record<string, string>;
    changed: { label: string; adjustments: SmsTextAdjustmentType[] }[];
    tooLong: { label: string; length: number }[];
    // Messages with more pictures than their channel can carry - these block the send too.
    tooLarge: { label: string; problem: string }[];
  } | null>(null);

  /*
    What the SMS view was filled with when it opened - or last carried back into the email Body -
    and null while the Email view is on screen. Compared with the view's fields to tell whether the
    sender changed anything there: only then is the Body rewritten from it, so merely looking at the
    SMS view never costs the Body its formatting.
  */
  const smsDerived = useRef<SmsSnapshotType | null>(null);

  // The same, for the open row's Custom message while it is edited as a text (see BatchRowPane).
  const rowSmsDerived = useRef<SmsSnapshotType | null>(null);

  // The row's default contacts, fetched only when a row is opened. Separate from customRecipients
  // so "opened a row" and "edited its recipients" stay distinguishable - only the latter is sent
  // explicitly, and only the latter marks the row Custom.
  const [loadedRecipients, setLoadedRecipients] = useState<Record<string, { to: any[]; cc: any[] }>>(
    {}
  );
  /*
    Recipients the sender added by hand, held across a channel change.

    Pinning a channel re-resolves the row's DEFAULT contacts on it (see handleRowSendAsChange), and
    that answer arrives from the server a moment later - so anyone the sender had added themselves
    has to be parked here and merged back in when it lands, or the reseed would wipe them.
  */
  const [keepOnReload, setKeepOnReload] = useState<{
    row: string;
    to: RecipientOptionType[];
    cc: RecipientOptionType[];
  } | null>(null);

  // Bumped when the open row's contacts are edited; the only signal RHFMessageRecipients takes to
  // refetch its address list.
  const [contactsUpdate, setContactsUpdate] = useState<Date | null>(null);
  // Latest-value mirror, read by the form subscription below - see the comment there.
  const loadedRecipientsRef = useRef(loadedRecipients);
  loadedRecipientsRef.current = loadedRecipients;

  // The rows taken out of the batch. Stored as exclusions so every row starts included without an
  // effect to seed it (see `included` below).
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [selectedKey, setSelectedKey] = useState<string>(DEFAULT_KEY);
  // Per-row message overrides, and per-row recipient edits. Kept apart because a row can have its
  // own To/Cc while still sending the batch's message. A row missing from BOTH maps is sent exactly
  // as the batch defines it, and its recipients are resolved server-side - which is why neither map
  // is pre-populated.
  const [overrides, setOverrides] = useState<
    Record<string, { subject: string; content: string; textContent: string }>
  >({});
  const [customRecipients, setCustomRecipients] = useState<Record<string, { to: any[]; cc: any[] }>>(
    {}
  );

  // Paging is the listing's, not the batch's: a batch mails every customer the filters match.
  const listParams = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { pageSize, pageNumber, ...rest } = requestMetadata?.params ?? {};
    return rest;
  }, [requestMetadata]);

  const { data: customers, isLoading, isError, error } = useRetrieve({
    url: 'Utilities/CustomerMessage/Batch/Customers',
    params: listParams,
  });

  /*
    The endpoint answers in this page's own words (entityId/customerName); the batch components
    speak the neutral row shape, so the mapping happens once, here.
  */
  const rows: IBatchRow[] = useMemo(
    () =>
      (customers?.result ?? []).map((customer: any) => ({
        key: String(customer.entityId),
        label: customer.customerName,
        id: customer.entityId,
        emailContactCount: customer.emailContactCount,
        phoneContactCount: customer.phoneContactCount,
        emailReachableCount: customer.emailReachableCount,
        phoneReachableCount: customer.phoneReachableCount,
      })),
    [customers]
  );

  const rowKeys = useMemo(() => rows.map((r) => r.key), [rows]);

  const included = useMemo(
    () => new Set(rowKeys.filter((key) => !excluded.has(key))),
    [rowKeys, excluded]
  );

  const selectedRow = selectedKey === DEFAULT_KEY ? null : selectedKey;

  /*
    What the recipient field is seeded with - and the ONLY thing it can be seeded with.

    RHFMessageRecipients re-seeds to/cc from these arrays whenever their identity changes, which
    means the merge with the sender's own additions has to happen HERE. Doing it with setValue
    where the defaults land was not enough: storing the new defaults changed this array's identity
    a moment later, and the field re-seeded itself from the defaults alone - so an addition kept
    across a channel change reappeared and then vanished.

    Memoized for the same reason: a fresh literal each render would re-seed continuously and
    clobber whatever the sender just typed.
  */
  const emptyRecipients = useRef({ to: [] as any[], cc: [] as any[] }).current;
  const rowRecipients = useMemo(() => {
    if (!selectedRow) return emptyRecipients;

    const base = customRecipients[selectedRow] ?? loadedRecipients[selectedRow];
    if (!base) return emptyRecipients;

    const keep = keepOnReload?.row === selectedRow ? keepOnReload : null;
    if (!keep) return base;

    // Deduped on the address, since someone the sender added by hand can also be one of the
    // defaults on the channel just pinned.
    const baseIds = new Set(base.to.map((option: RecipientOptionType) => String(option.id)));

    return {
      to: [...base.to, ...keep.to.filter((option) => !baseIds.has(String(option.id)))],
      cc: keep.cc,
    };
  }, [selectedRow, customRecipients, loadedRecipients, keepOnReload, emptyRecipients]);

  /*
    What a row's To/CC boxes hold while the sender has not touched them since they were seeded.

    For the open row that is the seed above, not its edits-or-defaults: after a channel change the
    seed also carries whoever the sender had added by hand. The watcher reports only a difference
    from the seed, so reading the defaults here judged the row on an empty To - "No recipient
    selected" beside a To box with an address in it. Until the row's addresses arrive the seed is
    empty for want of them, so the counts keep answering, as for any row nobody has opened.
  */
  const untouchedRecipients = (key: string) =>
    key === selectedRow && rowRecipients !== emptyRecipients
      ? rowRecipients
      : customRecipients[key] ?? loadedRecipients[key];

  /*
    Which channel each row's message goes on, or why it has none yet.

    Resolved here rather than in the list item because it depends on the dialog's own row maps - a
    pinned Send As, and the recipients of a row the user edited. A row is judged on its edited
    recipients when it has them, and otherwise on the per-channel counts the list endpoint gave.
  */
  const resolvedRows = new Map<string, RowChannelType>(
    rows.map((row) => [
      row.key,
      getRowChannel(
        row,
        CUSTOMER_TERMS,
        rowChannels[row.key] ?? 'auto',
        /*
          Whatever is in the To/CC boxes is what the badge must agree with, so the freshest answer
          wins: the OPEN row's live recipients first, then what its boxes hold untouched (see
          untouchedRecipients). The per-channel counts behind those are only a stand-in for rows
          nobody has opened - they were taken when the dialog opened, and neither editing a row's
          contacts nor selecting a different address refetches the list.
        */
        openRow?.rowKey === row.key
          ? { to: openRow.to, cc: openRow.cc }
          : untouchedRecipients(row.key)
      ),
    ])
  );

  const resolveRow = (row: IBatchRow) => resolvedRows.get(row.key)!;

  const unresolvedRows = rows.filter((r) => included.has(r.key) && !!resolveRow(r).reason);

  const selectedRowData = selectedRow ? rows.find((r) => r.key === selectedRow) ?? null : null;

  const rowContentRef = useRef<string>('');

  /*
    The open row's Custom state, live.

    overrides/customRecipients are only written when a row is left (see commitCurrent), which is
    correct for the body - Lexical's HTML is expensive to serialise on every keystroke - but it left
    the chip a step behind. So the flags that drive the chip are tracked separately and updated on
    every change, while the body is still only serialised on commit.

    Tagged with the row it describes. During a switch the subscription below still closes over the
    OUTGOING row while setValue pushes the INCOMING row's values through it, so an untagged flag
    would be computed from one row's values and read against another's - marking a pristine row
    Custom.
  */
  const [liveRow, setLiveRow] = useState<{
    row: string | null;
    useDefault: boolean;
    recipientsEdited: boolean;
  }>({ row: null, useDefault: true, recipientsEdited: false });

  useEffect(() => {
    if (!formMethods) return undefined;

    const subscription = formMethods.watch((values, { name }) => {
      // Mirrored out of RHF on every keystroke so committing a row never depends on RHF having
      // flushed by the time the click handler runs.
      if (name === 'rowContent') rowContentRef.current = values.rowContent ?? '';

      if (!selectedRow) return;

      // Through the ref, never the closure: re-seeding a row (a contacts edit refetches its
      // defaults) writes new defaults AND new form values in the same commit, and a subscription
      // still holding the previous defaults would compare the new addresses against the old ones
      // and label a freshly-defaulted row as edited.
      const useDefault = !!values.useDefault;
      const recipientsEdited = isRecipientsEdited(values, loadedRecipientsRef.current[selectedRow]);

      // Bail when nothing actually changed. This fires on every keystroke in the row's Subject and
      // Body, and a new object each time would re-render the whole row list behind it.
      setLiveRow((prev) =>
        prev.row === selectedRow &&
        prev.useDefault === useDefault &&
        prev.recipientsEdited === recipientsEdited
          ? prev
          : { row: selectedRow, useDefault, recipientsEdited }
      );
    });

    return () => subscription.unsubscribe();
  }, [formMethods, selectedRow]);

  /*
    What a row has of its own, reported as two independent facts rather than one "customized" flag:
    the list labels them separately (To/CC vs Msg) because they are separately undoable and answer
    different questions - "who is this going to" and "what does it say".
  */
  /*
    A pinned channel is reported separately from edited recipients, though both change who the
    message reaches: the list shows one or the other (see BatchRowList), and which one it is tells
    the sender whether they chose those addresses or merely chose the channel they are on.
  */
  const isChannelPinned = (key: string) => (rowChannels[key] ?? 'auto') !== 'auto';

  const getRowCustom = (key: string) =>
    key === selectedRow && key === liveRow.row
      ? {
          recipients: liveRow.recipientsEdited,
          message: !liveRow.useDefault,
          channel: isChannelPinned(key),
        }
      : {
          recipients: !!customRecipients[key],
          message: !!overrides[key],
          channel: isChannelPinned(key),
        };

  const selectedCustom = selectedRow ? getRowCustom(selectedRow) : null;

  // A row's default contacts, loaded the first time it is opened. Nothing is fetched for rows the
  // user never customizes - the server resolves those itself at send time.
  /*
    Addressed on the row's pinned channel when it has one, so the pane shows the addresses the send
    will actually use. On Auto there is no channel to impose and the contacts answer for themselves.
  */
  const pinnedChannel = selectedRow ? rowChannels[selectedRow] : undefined;
  // Undefined on Auto, which is what leaves the contacts to answer for themselves.
  const pinnedChannelParam = CHANNEL_PARAM[pinnedChannel ?? 'auto'];

  const { data: rowDefaults, refetch: refetchRowDefaults } = useRetrieve({
    url: `Utilities/CustomerMessage/Defaults/${selectedRow ?? 0}`,
    params: { channel: pinnedChannelParam },
    enabled: !!selectedRow && !loadedRecipients[selectedRow],
  });

  /*
    Keyed on the response rather than "have I loaded this row yet", so a refetch lands.

    Editing a row's contacts re-enables the query above, and with the app-wide staleTime of 0 that
    serves the cached (pre-edit) defaults first and refetches behind it. Bailing once the row was in
    loadedRecipients would leave the pane showing exactly that stale serve.
  */
  useEffect(() => {
    if (!selectedRow || !rowDefaults) return;

    const loaded = { to: rowDefaults.to ?? [], cc: rowDefaults.cc ?? [] };
    setLoadedRecipients((prev) => ({ ...prev, [selectedRow]: loaded }));

    // Only seed the form if this row is still the one on screen and the user has not already
    // edited it.
    if (!customRecipients[selectedRow]) {
      /*
        Only the defaults. Anything the sender added by hand is merged in by rowRecipients above,
        which is what the field actually seeds from - see the comment there.
      */
      formMethods?.setValue('to', loaded.to);
      formMethods?.setValue('cc', loaded.cc);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRow, rowDefaults]);

  const syncContentFromEditor = () => {
    if (!masterEditor || !formMethods) return;

    setTimeout(
      () =>
        formMethods.setValue('content', editorToEmailHtml(masterEditor), { shouldValidate: false }),
      0
    );
  };

  /*
    The SMS view filled from the email Body: its words, and its pictures as the text's attachments.
    On every visit, since the Body may have changed meanwhile - which loses nothing, because whatever
    the sender changed in the view was carried back into the Body when they left it.
  */
  const syncSmsFromBody = (): SmsSnapshotType | null => {
    if (!masterEditor || !formMethods) return null;

    const { text, images } = splitBodyForSms(masterEditor);

    formMethods.setValue('textContent', text, { shouldValidate: true });
    formMethods.setValue('smsImages', images);

    return { text, images };
  };

  /*
    The other direction, and automatic: once the sender has changed anything in the SMS view, the
    email Body is rewritten from it - the words, then the pictures - so the emails say what the texts
    say. Done at each moment something reads the Body (leaving the view, opening a row, Send) rather
    than on every keystroke; the Body is out of sight meanwhile, so the sender sees no difference.
  */
  const syncBodyFromSms = () => {
    const derived = smsDerived.current;
    if (!masterEditor || !formMethods || !derived) return;

    const sms: SmsSnapshotType = {
      text: formMethods.getValues('textContent') ?? '',
      images: formMethods.getValues('smsImages') ?? [],
    };
    if (!smsChangedSince(sms, derived)) return;

    replaceBodyWithSms(masterEditor, sms);
    formMethods.setValue('content', editorToEmailHtml(masterEditor), { shouldValidate: false });
    smsDerived.current = sms;
  };

  const showBatchView = (view: SendChannelType) => {
    if (view === 'sms') smsDerived.current = syncSmsFromBody();
    else {
      syncBodyFromSms();
      smsDerived.current = null;
    }

    setBatchChannel(view);
  };

  const onBatchChannelChange = (
    _: React.MouseEvent<HTMLElement>,
    value: SendChannelType | null
  ) => {
    if (value && value !== batchChannel) showBatchView(value);
  };

  /*
    Pin (or unpin) the open row's channel.

    Pruning happens HERE rather than in an effect watching the channel: this form is one scratchpad
    shared by whichever row is open, so an effect would also fire as selectItem swaps the next row
    in and would prune the row that just left, using its predecessor's chips.
  */
  const handleRowSendAsChange = (sendAs: SendAsType) => {
    if (!selectedRow) return;

    setRowChannels((prev) => ({ ...prev, [selectedRow]: sendAs }));

    if (!formMethods) return;

    /*
      Pinning rebuilds the row's recipients rather than filtering them.

      The two halves are treated differently because they mean different things:

        * the DEFAULT contacts are re-resolved on the new channel. They were addressed on the
          channel each contact prefers, so merely filtering them to the pinned one throws all of
          them away and leaves the row empty - the row then sends to nobody, which is exactly what
          pinning was meant to prevent. Dropping the loaded set re-enables the query above, which
          now asks for this channel, and the effect below seeds the answer.

        * anyone the sender ADDED by hand is theirs, and is kept - minus whoever the new channel
          cannot reach, since there is no other address to fall back to for them. They are parked
          in keepOnReload because the re-resolved defaults arrive asynchronously and would
          otherwise overwrite them.

      A default the sender had REMOVED comes back, because the two channels address a contact
      differently and an option's id is its address, so there is nothing to match the removal
      against on the other side.
    */
    const values = formMethods.getValues();
    const defaultIds = new Set(
      (loadedRecipients[selectedRow]?.to ?? []).map((option: RecipientOptionType) => String(option.id))
    );

    const reachable = (list: RecipientOptionType[] = []) =>
      sendAs === 'auto' ? list : list.filter((option) => supportsChannel(option, sendAs));

    const addedTo = reachable(
      (values.to ?? []).filter((option: RecipientOptionType) => !defaultIds.has(String(option.id)))
    );
    // CC is never defaulted (DefaultInclude means To), so all of it is the sender's own.
    const keptCc = reachable(values.cc ?? []);

    setKeepOnReload({ row: selectedRow, to: addedTo, cc: keptCc });
    // Both maps, so the reseed below runs for a row whose edits were already committed too.
    setCustomRecipients((prev) => withoutKey(prev, selectedRow));
    setLoadedRecipients((prev) => withoutKey(prev, selectedRow));

    formMethods.setValue('to', addedTo);
    formMethods.setValue('cc', keptCc);
  };

  // Only on the Email view, where the editor's toolbar is - the SMS view is filled afresh on its
  // next visit.
  const clearBody = () => {
    if (masterEditor) {
      htmlToEditorState(masterEditor, '');
      syncContentFromEditor();
    }
  };

  /*
    The open row's SMS editor, filled from its Custom message - the row's own rich body, in the
    override editor - just as the email Body fills the batch's SMS view.
  */
  const syncRowSmsFromBody = () => {
    if (!overrideEditor || !formMethods) return;

    const { text, images } = splitBodyForSms(overrideEditor);

    formMethods.setValue('rowTextContent', text);
    formMethods.setValue('rowSmsImages', images);
    rowSmsDerived.current = { text, images };
  };

  // And back, automatically, once the sender has changed anything there - see syncBodyFromSms.
  const syncRowBodyFromSms = () => {
    const derived = rowSmsDerived.current;
    // A row on the default message has no message of its own to carry anything into.
    if (!overrideEditor || !formMethods || !derived || formMethods.getValues('useDefault')) return;

    const sms: SmsSnapshotType = {
      text: formMethods.getValues('rowTextContent') ?? '',
      images: formMethods.getValues('rowSmsImages') ?? [],
    };
    if (!smsChangedSince(sms, derived)) return;

    replaceBodyWithSms(overrideEditor, sms);
    rowContentRef.current = editorToEmailHtml(overrideEditor);
    formMethods.setValue('rowContent', rowContentRef.current, { shouldValidate: false });
    rowSmsDerived.current = sms;
  };

  /*
    Whether the open row goes out as a text - pinned to SMS, or on Auto with recipients that resolve
    to it - which is when its Custom message is edited in the SMS editor (see BatchRowPane). The same
    resolver that picks the channel the row is sent on, so the editor shown is the channel used.
  */
  const rowSmsEditing =
    !!selectedRowData &&
    ((rowChannels[selectedRowData.key] ?? 'auto') === 'sms' ||
      resolveRow(selectedRowData).channel === 'sms');
  const rowCustomized = !!selectedRow && liveRow.row === selectedRow && !liveRow.useDefault;

  /*
    The open row's editor follows its channel: into the SMS editor when the row goes out as a text,
    and - carrying anything changed there into the row's message - back out when it no longer does.
    Read behind the override editor's pending update, so a row seeded a moment ago is read as
    seeded, not as the row before it.
  */
  useEffect(() => {
    // Not the instance that unmounted with the row pane: the one mounting now reports itself next.
    if (!overrideEditor?.getRootElement() || !formMethods) return;

    if (!rowCustomized) {
      rowSmsDerived.current = null;
      return;
    }

    if (rowSmsEditing) {
      if (!rowSmsDerived.current) overrideEditor.update(() => {}, { onUpdate: syncRowSmsFromBody });
      return;
    }

    syncRowBodyFromSms();
    rowSmsDerived.current = null;
    // The two syncs are render-scoped functions over the same editor and form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRow, rowSmsEditing, rowCustomized, overrideEditor, formMethods]);

  /*
    Stash whatever is on screen for the row being left.

    Called synchronously from the click handler, BEFORE the editor is repointed: htmlToEditorState
    clears the editor root, and its own update echoes back through RHF - so committing any later
    (in an effect keyed on the selection, say) would write the incoming row's body onto the
    outgoing row's key.

    Returns the overrides as committed, because the Send click reads them in the same handler:
    setOverrides only lands on the next render.
  */
  const commitCurrent = () => {
    if (!selectedRow || !formMethods) return overrides;

    // The row's SMS editor into its message first, while the override editor still holds this row.
    syncRowBodyFromSms();

    const values = formMethods.getValues();

    setCustomRecipients((prev) =>
      isRecipientsEdited(values, loadedRecipients[selectedRow])
        ? { ...prev, [selectedRow]: { to: values.to ?? [], cc: values.cc ?? [] } }
        : withoutKey(prev, selectedRow)
    );

    /*
      The same message as plain text, for a row that goes out as one: as the sender wrote it, when
      they wrote it in the row's SMS editor, and otherwise derived HERE, while the override editor
      still holds this row - splitBodyForSms needs the live LexicalEditor, not the HTML string
      below, so there is no deriving it later.

      Kept for every row, not just the SMS ones, because a row's channel can still change after it
      was written.
    */
    let textContent = '';
    if (rowSmsDerived.current) textContent = values.rowTextContent ?? '';
    else if (overrideEditor) textContent = splitBodyForSms(overrideEditor).text;

    const nextOverrides = values.useDefault
      ? withoutKey(overrides, selectedRow)
      : {
          ...overrides,
          [selectedRow]: {
            subject: values.rowSubject ?? '',
            content: rowContentRef.current,
            textContent,
          },
        };

    setOverrides(nextOverrides);

    return nextOverrides;
  };

  /*
    Point the (hidden) override editor at one row's message.

    An override starts from the batch's body - the point is to tweak this customer's copy, not to
    retype it. Unlike SCE Reports there is nothing to stamp into it: the body carries no per-row
    placeholders.
  */
  const seedOverride = (rowKey: string) => {
    const existing = overrides[rowKey];
    const body = existing?.content ?? formMethods?.getValues('content') ?? '';

    formMethods?.setValue('rowSubject', existing?.subject ?? formMethods?.getValues('subject'));
    rowContentRef.current = body;
    /*
      Into the form as well. Opened from the default message, the row pane mounts - and with it a
      NEW editor, which starts from this field rather than from the instance seeded below (that one
      went with the pane). Without it the row showed the body of whichever row was edited last.
    */
    formMethods?.setValue('rowContent', body);
    if (overrideEditor) htmlToEditorState(overrideEditor, body);
  };

  const selectItem = (nextKey: string) => {
    /*
      Re-selecting the open row is a no-op. Going through the switch below would re-seed the form
      from this render's maps, which do not yet hold the commit just queued.
    */
    if (nextKey === selectedKey) return;

    // Leaving the batch's own message: a row's Custom message starts as a copy of its email Body,
    // so the SMS view is carried back into that Body first.
    syncBodyFromSms();

    commitCurrent();
    // The next row's SMS editor is read afresh from its own message.
    rowSmsDerived.current = null;

    // The additions parked by a channel change belong to the row being left - see the reseed above.
    setKeepOnReload(null);

    if (nextKey !== DEFAULT_KEY) {
      const override = overrides[nextKey];
      const recipients = customRecipients[nextKey] ?? loadedRecipients[nextKey];

      formMethods?.setValue('to', recipients?.to ?? []);
      formMethods?.setValue('cc', recipients?.cc ?? []);
      formMethods?.setValue('useDefault', !override);

      // Only when the row actually has an override to show. Seeding eagerly meant a full HTML ->
      // Lexical parse on every click in the list, for an editor nobody is looking at; unchecking
      // "use default message" seeds it at the moment it becomes visible.
      if (override) seedOverride(nextKey);
      else formMethods?.setValue('rowSubject', formMethods?.getValues('subject'));

      // Last, so it wins over the setValue calls above - each of those fires the watch
      // subscription, which is still closed over the OUTGOING row.
      setLiveRow({
        row: nextKey,
        useDefault: !override,
        recipientsEdited: !!customRecipients[nextKey],
      });
    }

    setSelectedKey(nextKey);
  };

  /*
    selectItem closes over most of the dialog's state, so it is a new function every render. The row
    list is memoized on its props, so it takes this stable wrapper instead; the ref keeps it pointed
    at the current closure.
  */
  const selectItemRef = useRef(selectItem);
  selectItemRef.current = selectItem;
  const handleSelectItem = useCallback((key: string) => selectItemRef.current(key), []);

  // Unchecking "use default" starts the override from the batch's message rather than a blank
  // editor.
  const handleUseDefaultChange = (useDefault: boolean) => {
    formMethods?.setValue('useDefault', useDefault);
    if (!selectedRow) return;

    // Set alongside the subscription rather than relying on it: the effect re-subscribes on the
    // selection, so a toggle immediately after a switch could otherwise land on the old closure.
    setLiveRow((prev) => ({ ...prev, row: selectedRow, useDefault }));

    if (useDefault) return;

    seedOverride(selectedRow);
  };

  /*
    The open row's contacts were edited, so everything derived from the old ones is stale: the row's
    default To/Cc, and the address pick-list inside RHFMessageRecipients (which only refetches when
    its `update` prop changes identity).

    Dropping the row from customRecipients is deliberate: a contacts edit resets that row to the new
    defaults - the same rule the single-send dialog follows - so its To/CC label clears too.
  */
  const handleContactsChanged = () => {
    if (!selectedRow) return;

    setCustomRecipients((prev) => withoutKey(prev, selectedRow));

    // refetch() rather than dropping the row from loadedRecipients to re-enable the query: react-
    // query serves the cached (pre-edit) defaults on an enable-flip, and in the gap before the
    // network answers the pane would blank the To field.
    refetchRowDefaults();

    setContactsUpdate(new Date());
    setLiveRow((prev) => ({ ...prev, row: selectedRow, recipientsEdited: false }));
  };

  // Puts the row back exactly as it arrived - default message AND default recipients. Reverting
  // only the message would leave the row still Custom for edits the user cannot see from the list.
  const handleRevert = () => {
    if (!selectedRow) return;

    const loaded = loadedRecipients[selectedRow];

    setOverrides((prev) => withoutKey(prev, selectedRow));
    setCustomRecipients((prev) => withoutKey(prev, selectedRow));
    // Back to Auto too: "default" means everything this row overrode.
    setRowChannels((prev) => withoutKey(prev, selectedRow));
    // Including anyone a channel change had kept: reverting is back to the defaults alone.
    setKeepOnReload(null);

    formMethods?.setValue('useDefault', true);
    formMethods?.setValue('to', loaded?.to ?? []);
    formMethods?.setValue('cc', loaded?.cc ?? []);

    setLiveRow({ row: selectedRow, useDefault: true, recipientsEdited: false });
  };

  // A send refused because RingCentral is not connected starts connecting when OK is pressed.
  const { connectIfNotConnected } = useConnectRingCentral();

  const { mutate, isPending } = useAddMutation({
    url: 'Utilities/CustomerMessage/Batch/Send',
    onErrorDismiss: connectIfNotConnected,
    onSuccess: (response: any) => {
      onSuccessEnqueueSnackbar(response);
      queryClient.invalidateQueries({ queryKey: [PENDING_EMAILS_QUERY_KEY] });
      onClose();
    },
  });

  // Validated here rather than in the Yup schema: the schema only ever sees the form's own fields,
  // and this is about the row map beside it. A row the user opened and cleared must block the send -
  // distinct from a row never opened, which carries no addresses on purpose and is resolved
  // server-side.
  const emptiedRows = rows
    .filter((row) => {
      if (!included.has(row.key)) return false;

      const custom = customRecipients[row.key];
      const { channel } = resolveRow(row);

      // Counted after resolving addresses, not before: a row whose only recipients cannot be
      // reached on its channel is just as empty as one the user cleared. An unresolved row is
      // already blocking on its own account, so it is not counted twice.
      return custom && channel ? !addressesFor(custom.to, channel).length : false;
    })
    .map((row) => row.key);

  /*
    Both kinds of broken row under one count: the ones that cannot decide a channel, and the ones
    that decided but hold no address the channel can reach. They are disjoint by construction -
    getRowChannel answers with a channel or a reason, never both - so this is a plain sum.
  */
  const invalidRowCount = unresolvedRows.length + emptiedRows.length;

  // The message list's All / Invalid / Excluded views. Invalid is exactly the rows counted above.
  const {
    listView,
    changeListView,
    visibleRows,
    invalidCount,
    excludedCount,
    navigableKeys,
    formatNavLabel,
  } = useBatchListView({
    rows,
    included,
    invalidKeys: new Set([...unresolvedRows.map((row) => row.key), ...emptiedRows]),
    selectedKey,
    onSelect: handleSelectItem,
  });

  /*
    Every body that will actually leave as a text: the batch's own, if any included row falls back
    to it, plus each SMS row that carries its own. Only these are rewritten - an email body is not
    scored by a carrier.
  */
  const smsRows = rows.filter((row) => included.has(row.key) && resolveRow(row).channel === 'sms');

  const smsBlocked = smsRows.length > 0 && !ringCentralConnected;

  // Whether any included message goes out by email - the only kind with a Subject.
  const sendsEmail = rows.some(
    (row) => included.has(row.key) && resolveRow(row).channel === 'email'
  );

  // Takes the overrides commitCurrent just produced - see there for why state would be stale here.
  const prepareSend = (committedOverrides: typeof overrides) => {
    // A customized row whose body has no words of its own falls back to the batch's text, exactly
    // as the server reads it.
    const ownText = (key: string) => {
      const text = committedOverrides[key]?.textContent;
      return text?.trim() ? text : null;
    };

    const rowBodies = makeBodiesSmsSpamSafe(
      smsRows
        .filter((row) => ownText(row.key) !== null)
        .map((row) => ({ label: row.label, text: ownText(row.key)! }))
    );

    const usesBatchText = smsRows.some((row) => ownText(row.key) === null);

    /*
      The texts say what the email Body says. On the Email view the text is translated from the
      Body here - what makes it necessary is that a ROW is a text, not that the sender happened to
      open the SMS view. On the SMS view the text IS the message, as the sender left it; the Body
      was rewritten from it when Send was pressed.
    */
    if (usesBatchText && batchChannel === 'email') syncSmsFromBody();

    // setValue is synchronous, so the text derived above is readable here.
    const batchText = formMethods?.getValues('textContent') ?? '';
    const batch = usesBatchText ? makeSmsSpamSafe(batchText) : null;
    const finalBatchText = batch?.text ?? batchText;

    /*
      The carrier's length cap, checked on the bodies as they will actually be sent - after the
      spam-safe rewrite, which lengthens them ("$1,000" becomes "1,000 dollars").
    */
    const tooLong = [
      ...(usesBatchText ? [{ label: DEFAULT_BODY_LABEL, length: finalBatchText.length }] : []),
      ...rowBodies.bodies.map((body) => ({ label: body.label, length: body.text.length })),
    ].filter((body) => body.length > MAX_SMS_TEXT_LENGTH);

    // Keyed by row, but labelled by customer name - the confirmation names messages the way the
    // list does.
    const rowTexts: Record<string, string> = {};
    smsRows
      .filter((row) => ownText(row.key) !== null)
      .forEach((row) => {
        const rewritten = rowBodies.bodies.find((b) => b.label === row.label);
        rowTexts[row.key] = rewritten?.text ?? ownText(row.key)!;
      });

    // Pictures are budgeted per message, on the channel it goes out on - see findOversizedMessages.
    // A customized row whose body was cleared goes out on the default's, as the server reads it.
    const tooLarge = findOversizedMessages(
      rows
        .filter((row) => included.has(row.key))
        .map((row) => ({
          label: row.label,
          ownBody: committedOverrides[row.key]?.content?.trim()
            ? committedOverrides[row.key].content
            : undefined,
          channel: resolveRow(row).channel ?? 'email',
        })),
      formMethods?.getValues('content') ?? ''
    );

    setPendingSend({
      batchText: finalBatchText,
      usesBatchText,
      rowTexts,
      changed: [
        ...(batch?.adjustments.length
          ? [{ label: DEFAULT_BODY_LABEL, adjustments: batch.adjustments }]
          : []),
        ...rowBodies.changed,
      ],
      tooLong,
      tooLarge,
    });

    confirmDialog.setTrue();
  };

  const handleSend = async () => {
    const values = formMethods?.getValues();

    // Closed first: the pictures below take a moment, and the confirmation must not be pressed twice.
    confirmDialog.setFalse();

    // Applied here rather than before the confirmation, so cancelling leaves the sender's words
    // exactly as they wrote them. A rewrite for the carriers is the texts' alone, so it is not
    // counted as an edit that would carry into the emails.
    if (pendingSend?.usesBatchText && pendingSend.changed.length) {
      formMethods?.setValue('textContent', pendingSend.batchText, { shouldValidate: true });
      if (smsDerived.current) {
        smsDerived.current = { ...smsDerived.current, text: pendingSend.batchText };
      }
    }

    const sending = rows.filter((row) => included.has(row.key));

    /*
      Every body in the batch in one pass, so a picture shared by the default message and the rows
      customized from it - each a copy of the default's body, pictures and all - is sent once. A
      text's pictures are among them: it attaches them from the rich body it would be sent from.
    */
    const {
      bodies: [content, ...rowContents],
      images,
    } = await toTransport([values?.content, ...sending.map((row) => overrides[row.key]?.content)]);

    mutate({
      // One for the batch: the server records it on every message it sends.
      category: values?.category,
      subject: values?.subject,
      content,
      images,
      // The plain-text half of the batch's message, only when some text falls back to it.
      textContent: pendingSend?.usesBatchText ? pendingSend.batchText : undefined,
      // What the recipient list was drawn from, recorded on the batch for audit.
      dataSourceUrl: `${
        process.env.REACT_APP_API_URL
      }Utilities/CustomerMessage/Batch/Customers?${new URLSearchParams(
        toQueryPairs(listParams)
      ).toString()}`,
      messages: sending.map((row, index) => {
        const override = overrides[row.key];
        const custom = customRecipients[row.key];

        // Send is blocked while any included row is unresolved, so by here every one has an
        // answer; email is a defensive default rather than a decision.
        const channel = resolveRow(row).channel ?? 'email';

        return {
          entityId: row.id,
          // The server obeys this rather than working it out again.
          channel: channel === 'sms' ? 'Text' : 'Email',
          // Undefined, not [], for untouched rows - the server reads null as "use this customer's
          // default contacts", already partitioned by the channel above.
          to: custom ? addressesFor(custom.to, channel) : undefined,
          cc: custom ? addressesFor(custom.cc, channel) : undefined,
          subject: override?.subject,
          // Undefined for a row on the default message, as before; its pictures as references.
          content: rowContents[index],
          // Only a text carries one. An email row's derived text is never sent, so its length
          // cannot refuse the batch.
          textContent:
            channel === 'sms' ? pendingSend?.rowTexts[row.key] ?? override?.textContent : undefined,
        };
      }),
    });
  };

  // Stable identity, so the memoized list rows do not re-render when anything else in the dialog does.
  const toggleInclude = useCallback(
    (key: string) =>
      setExcluded((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }),
    []
  );

  // Only the keys given - the rows the list shows - so a filtered view's All / None stays inside it.
  const includeAll = (include: boolean, keys: string[]) =>
    setExcluded((prev) => {
      const next = new Set(prev);
      keys.forEach((key) => (include ? next.delete(key) : next.add(key)));
      return next;
    });

  if (isLoading) return <CasLoaderWithBackdrop />;
  if (isError) throw new Error(error.message);

  return (
    <>
      <CasBaseDialog
        open
        maxWidth="xl"
        onClose={onClose}
        title="Batch Send"
        actions={
          <>
            {invalidRowCount > 0 && (
              <Typography variant="caption" sx={{ color: 'error.main', mr: 'auto' }}>
                {invalidRowCount} enabled {invalidRowCount === 1 ? 'message is' : 'messages are'}{' '}
                invalid
                {/* Beside the disabled Send, where the sender looks when it will not go. */}
                {listView !== 'invalid' && (
                  <>
                    {' '}
                    <CasLink onClick={() => changeListView('invalid')}>Review</CasLink>
                  </>
                )}
              </Typography>
            )}
            <CasSecondaryButton onClick={onClose}>Cancel</CasSecondaryButton>
            <CasButton
              onClick={async () => {
                // On the SMS view the text is the message, so the emails are rewritten from it first.
                syncBodyFromSms();

                // Whatever row is open has not been stashed yet - Send from inside a row must
                // include the edits that are on screen.
                const committedOverrides = commitCurrent();

                // Subject is required only when some message is an email - a text has none - so it
                // is checked here, where the rows' channels are known, rather than in the schema.
                const subjectMissing =
                  sendsEmail && !String(formMethods?.getValues('subject') ?? '').trim();

                // The form owns Subject/Body, so let it report its own errors before the
                // confirmation asks about anything else.
                const valid = await formMethods?.trigger();

                if (subjectMissing) {
                  formMethods?.setError('subject', {
                    type: 'manual',
                    message: 'Subject is required.',
                  });
                }

                if (!valid || subjectMissing) {
                  // Category, Subject and Body live on the Default message, which may not be on
                  // screen. An error on a hidden field made Send look like it did nothing.
                  const bodyError = formMethods?.getFieldState('content').error;
                  const defaultFieldInvalid =
                    subjectMissing ||
                    !!bodyError ||
                    formMethods?.getFieldState('subject').invalid ||
                    formMethods?.getFieldState('category').invalid;

                  if (defaultFieldInvalid && selectedRow) selectItem(DEFAULT_KEY);

                  /*
                    Both views show the Subject whenever an email goes out, which a missing one
                    implies. The Body is the one field the SMS view does not show - there, the email
                    Body is the text's own translation, so its problem is the text's and is said
                    there, on the SMS view the sender is looking at.
                  */
                  if (bodyError && batchChannel === 'sms') {
                    const message =
                      bodyError.type === 'required' ? 'Body is required.' : bodyError.message;
                    formMethods?.setError('textContent', { type: 'manual', message });
                  }
                  return;
                }

                prepareSend(committedOverrides);
              }}
              disabled={included.size === 0 || invalidRowCount > 0 || smsBlocked || isPending}
            >
              Send {included.size} Messages
            </CasButton>
          </>
        }
      >
        <CasReactHookForm
          schema={batchComposeSchema}
          // No default content: this window has nothing to say on the sender's behalf. No default
          // Category either - picking one is the sender's call (see RHFMessageCategory).
          defaultValues={{
            category: null,
            subject: '',
            content: '',
            useDefault: true,
            rowSubject: '',
            to: [],
            cc: [],
            smsImages: [],
            rowTextContent: '',
            rowSmsImages: [],
          }}
          setFormMethods={setFormMethods}
        >
          {/* First thing in the window: a text needs the sender's own RingCentral line connected. */}
          <RingCentralConnectAlert sx={{ mb: 1 }} />
          <Stack direction="row" sx={{ height: '70vh', minHeight: 0 }}>
            <Stack
              sx={{
                flex: '0 0 260px',
                minHeight: 0,
                borderRight: '1px solid',
                borderColor: 'divider',
              }}
            >
              <BatchRowList
                terms={CUSTOMER_TERMS}
                rows={rows}
                visibleRows={visibleRows}
                listView={listView}
                onListViewChange={changeListView}
                invalidCount={invalidCount}
                excludedCount={excludedCount}
                included={included}
                getRowCustom={getRowCustom}
                resolveRow={resolveRow}
                selectedKey={selectedKey}
                onSelect={handleSelectItem}
                onToggleInclude={toggleInclude}
                onIncludeAll={includeAll}
              />
            </Stack>

            <Stack sx={{ flexGrow: 1, minWidth: 0, pl: 2, minHeight: 0 }}>
              {/* Pane header: what is open on the left, how to move between panes on the right. */}
              <Stack direction="row" alignItems="center" spacing={1} sx={{ pb: 1 }}>
                <Typography variant="subtitle2">
                  {selectedRowData ? selectedRowData.label : 'Default message'}
                </Typography>

                {/* Only on the batch's own message: a row has recipients, so its channel is
                    resolved from them rather than chosen here. This picks which view of the
                    message is being edited. */}
                {!selectedRowData && (
                  <ToggleButtonGroup
                    value={batchChannel}
                    exclusive
                    size="small"
                    onChange={onBatchChannelChange}
                  >
                    {BATCH_CHANNEL_OPTIONS.map(({ id, name }) => (
                      <ToggleButton
                        key={id}
                        value={id}
                        sx={{ px: 1.5, py: 0.25, textTransform: 'none' }}
                      >
                        {name}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                )}

                {/* Per-customer, so it belongs to a row and never to the batch's own message. */}
                {selectedRowData && (
                  <CustomerContactsButton
                    entityId={selectedRowData.id ?? undefined}
                    label="Edit Contacts"
                    onChanged={handleContactsChanged}
                  />
                )}

                {/* "default", not "default message": this puts the recipients back too, so it is
                    offered for either kind of customization. */}
                {(selectedCustom?.recipients ||
                  selectedCustom?.message ||
                  selectedCustom?.channel) && (
                  <CasLink onClick={handleRevert}>Revert to default</CasLink>
                )}

                {/* Walks the list beside it as it is filtered, default message first. */}
                <Stack sx={{ ml: 'auto' }}>
                  <CasItemNavigator
                    value={selectedKey}
                    onChange={handleSelectItem}
                    options={navigableKeys}
                    formatLabel={formatNavLabel}
                  />
                </Stack>
              </Stack>

              <Stack sx={{ flexGrow: 1, minWidth: 0, minHeight: 0, overflowY: 'auto' }}>
                {/*
                  Both panes stay mounted and are toggled with display. Each owns a Lexical editor,
                  and Lexical only reads its initial state at mount - unmounting the row pane would
                  rebuild the editor (and its plugins and contexts) on every click in the list.
                */}
                <div style={{ display: selectedRow ? 'block' : 'none' }}>
                  {selectedRowData && (
                    <>
                      <BatchRowPane
                        label={selectedRowData.label}
                        optionsEntity="Utilities/CustomerMessage/Email/Select"
                        optionsParams={{ entityId: selectedRowData.id ?? undefined }}
                        selectedTo={rowRecipients.to}
                        selectedCC={rowRecipients.cc}
                        contactsUpdate={contactsUpdate}
                        excluded={!included.has(selectedRowData.key)}
                        onUseDefaultChange={handleUseDefaultChange}
                        onReadyEditor={setOverrideEditor}
                        sendAs={rowChannels[selectedRowData.key] ?? 'auto'}
                        unresolvedReason={resolveRow(selectedRowData).reason}
                        onSendAsChange={handleRowSendAsChange}
                        smsEditing={rowSmsEditing}
                      />
                      {/* Inside the form, so it can watch what the pane above is editing. */}
                      <BatchOpenRowWatcher
                        rowKey={selectedRowData.key}
                        seededTo={rowRecipients.to}
                        seededCC={rowRecipients.cc}
                        onChange={setOpenRow}
                      />
                    </>
                  )}
                </div>

                <div style={{ display: selectedRow ? 'none' : 'block' }}>
                  {/* Above the message it classifies, and in both of its views. */}
                  <RHFMessageCategory />

                  <Typography
                    variant="caption"
                    // MUI maps caption to a <span>, so bottom padding on it does nothing - it needs
                    // display:block before any spacing applies.
                    sx={{ display: 'block', lineHeight: 1.3, color: 'text.secondary', mb: 1.5 }}
                  >
                    Sent to every {CUSTOMER_TERMS.noun} ticked on the left, unless that row has a
                    message of its own.
                  </Typography>

                  {/* A text has no subject. */}
                  {batchChannel === 'email' && <CasRHFTextField name="subject" label="Subject" />}

                  {/*
                    Hidden rather than unmounted, exactly like the two panes above: Lexical only
                    reads its initial state at mount. It is also the message's Email view: the SMS
                    view is translated from it, and carried back into it.
                  */}
                  <Box sx={{ display: batchChannel === 'email' ? undefined : 'none' }}>
                    <CasRHFLexicalRichTextEditor
                      mode="advanced"
                      name="content"
                      label="Body"
                      helperText={
                        <>
                          Your{' '}
                          <Link
                            component="button"
                            type="button"
                            underline="always"
                            onClick={signatureDialog.setTrue}
                            // A <button> is vertical-align: middle and carries its own font metrics,
                            // which lifts it off the sentence's baseline.
                            sx={{
                              verticalAlign: 'baseline',
                              font: 'inherit',
                              letterSpacing: 'inherit',
                            }}
                          >
                            signature
                          </Link>{' '}
                          will be appended to the Body
                        </>
                      }
                      onReadyEditor={setMasterEditor}
                      onClear={clearBody}
                    />
                  </Box>

                  {batchChannel === 'sms' && (
                    <>
                      <SmsTextField picturesName="smsImages" />
                      <RingCentralFromNumber sx={{ mt: -1, mb: 1 }} />
                      {/*
                        The emails go out too, and this is theirs - the one part of the message
                        the text has no place for.
                      */}
                      {sendsEmail && (
                        <CasRHFTextField name="subject" label="Subject (for Emails only)" />
                      )}
                      <BatchSmsPictures />
                    </>
                  )}
                </div>
              </Stack>
            </Stack>
          </Stack>
        </CasReactHookForm>
      </CasBaseDialog>

      {signatureDialog.value && <MySignatureSettingsDialog onClose={signatureDialog.setFalse} />}

      {confirmDialog.value && (
        <CasConfirmationDialog
          open
          title="Send batch"
          confirmLabel={`Send ${included.size} Messages`}
          onCancel={confirmDialog.setFalse}
          onConfirm={handleSend}
          confirmDisabled={!!pendingSend?.tooLong.length || !!pendingSend?.tooLarge.length}
        >
          <div>
            {included.size === 1
              ? 'This sends 1 message.'
              : `This sends ${included.size} separate messages, one per ${CUSTOMER_TERMS.noun}.`}
          </div>
          {smsRows.length > 0 && (
            <div>
              {`${smsRows.length} of them ${
                smsRows.length === 1 ? 'goes out as a text' : 'go out as texts'
              }.`}
            </div>
          )}
          {rows.length > included.size && (
            <div>
              {`${rows.length - included.size} of the ${rows.length} listed will not be sent.`}
            </div>
          )}

          {/* Blocks the confirmation: the carrier would refuse these, after the batch was queued. */}
          {!!pendingSend?.tooLong.length && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                A text can be at most {MAX_SMS_TEXT_LENGTH} characters. Shorten these before sending:
              </Typography>
              {pendingSend.tooLong.map(({ label, length }) => (
                <Typography key={label} variant="caption" sx={{ display: 'block', pl: 1 }}>
                  {label}: {length} characters
                </Typography>
              ))}
            </Alert>
          )}

          {/* Blocks the confirmation too: Microsoft or the carrier would refuse these after queueing. */}
          {!!pendingSend?.tooLarge.length && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                These messages have more images than they can carry. Remove an image or paste a
                smaller one:
              </Typography>
              {pendingSend.tooLarge.map(({ label, problem }) => (
                <Typography key={label} variant="caption" sx={{ display: 'block', pl: 1 }}>
                  {label}: {problem}
                </Typography>
              ))}
            </Alert>
          )}

          {/*
            Shown before anything goes out, because the sender's own words are being edited. Grouped
            by message: in a batch most of these bodies are off screen, so saying only WHAT changed
            would leave them hunting for WHERE.
          */}
          {!!pendingSend?.changed.length && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Carriers score these as spam, so they will be reworded before sending:
              </Typography>
              {pendingSend.changed.map(({ label, adjustments }) => (
                <Box key={label} sx={{ pl: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {label}
                  </Typography>
                  <Box component="ul" sx={{ m: 0, mb: 0.5, pl: 2.5 }}>
                    {adjustments.map((adjustment) => (
                      <li key={`${adjustment.from}->${adjustment.to}`}>
                        <Typography variant="caption">
                          &quot;{adjustment.from}&quot; &rarr; &quot;{adjustment.to}&quot;
                        </Typography>
                      </li>
                    ))}
                  </Box>
                </Box>
              ))}
            </Alert>
          )}
        </CasConfirmationDialog>
      )}
    </>
  );
}
